import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useWorker } from "../context/WorkerContext";
import { WORKER_TOKEN_KEY } from "../constants/tokenKeys";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PENDING_STATUS = "pending";
const ALLOTED_STATUS = "alloted";
const RETURNED_STATUS = "returned";
const REFUNDED_STATUS = "refunded";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (date) => {
  if (!date) return "";
  try {
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const getAccessoryObj = (r) =>
  (r?.accessory && typeof r.accessory === "object" && r.accessory) ||
  (r?.accessoryId && typeof r.accessoryId === "object" && r.accessoryId) ||
  null;

const displayName = (field, fallback) => {
  if (!field) return fallback || "-";
  if (typeof field === "object") return field.name || field.title || fallback || "-";
  return fallback || "-";
};

const AccessoryLogo = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <span className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-400 shrink-0">
        {alt ? alt.charAt(0).toUpperCase() : "?"}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || "accessory"}
      onError={() => setErrored(true)}
      className="w-8 h-8 rounded-full object-cover border border-gray-200 shrink-0"
    />
  );
};

const normalize = (r) => {
  const accessoryObj = getAccessoryObj(r);
  return {
    _id: r._id,
    accessoryName:
      (accessoryObj && (accessoryObj.name || accessoryObj.title)) ||
      r.accessoryName ||
      "-",
    accessoryLogo: accessoryObj?.logo || r.accessoryLogo || null,
    companyName: displayName(r.companyId, r.companyName),
    assignedBy: displayName(r.AllotedBy, r.assignedBy),
    assignedOn: r.AllotedOn || null,
    allotedOn: r.AllotedEntryTime || null,
    originalValue: r.AllotedAmount ?? r.originalValue ?? 0,
    status: r.status,
    returnedOn: r.ReturnedOn || null,
    refundBy: r?.RefundedBy?.name || "",
    refundedOn: r.deductedEntryTime || null,
    refundedValue: r.ReturnedAmount ?? r.refundedValue ?? 0,
    remarks: r?.ReturnedRemarks || "",
  };
};

const MyEssentials = () => {
  const { user } = useWorker();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecords = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem(WORKER_TOKEN_KEY);
      if (!token) throw new Error("Authentication required");

      const res = await axios.post(
        `${BASE_URL}/onDemandAccessories/get-accessory-history/worker`,
        { workerId: user.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = res.data;
      if (!data.success) {
        throw new Error(data.message || "Failed to load Essentials");
      }
      setRecords(data.data || data.history || []);
    } catch (err) {
      console.error("Failed to load essentials:", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const assignedItems = useMemo(
    () =>
      records
        .filter(
          (r) => r.status === PENDING_STATUS || r.status === ALLOTED_STATUS
        )
        .map(normalize),
    [records]
  );

  const statusColors = {
    pending: "bg-amber-50 text-amber-600",
    alloted: "bg-blue-50 text-blue-600",
    returned: "bg-emerald-50 text-emerald-600",
    refunded: "bg-purple-50 text-purple-600",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f0f0f5",
        display: "flex",
        flexDirection: "column",
        paddingTop: "74px",
      }}
    >
      {/* ── Header (matches PaymentHistory layout) ───────────────── */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 20px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            left: "20px",
            background: "none",
            border: "none",
            fontSize: "28px",
            color: "#222",
            cursor: "pointer",
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ‹
        </button>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#111",
            margin: 0,
          }}
        >
          My Store
        </h2>
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          padding: "0 20px 32px",
          maxWidth: "640px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {loading ? (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "18px",
              padding: "40px 20px",
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Loading Essentials...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* ── Assigned Essentials ───────────────────────────── */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "18px",
                padding: "20px 18px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#111",
                  margin: "0 0 16px",
                }}
              >
                Assigned Essentials
              </p>

              {assignedItems.length === 0 ? (
                <p
                  style={{
                    fontSize: "13px",
                    color: "#777",
                    textAlign: "center",
                    padding: "24px 0",
                    margin: 0,
                  }}
                >
                  No Essentials assigned to you.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {assignedItems.map((item) => (
                    <div
                      key={item._id}
                      style={{
                        backgroundColor: "#f7f7fa",
                        border: "1px solid #eee",
                        borderRadius: "12px",
                        padding: "14px 16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                          }}
                        >
                          <AccessoryLogo
                            src={item.accessoryLogo}
                            alt={item.accessoryName}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#222",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.accessoryName}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#222",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ₹{item.originalValue ?? 0}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "10px",
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        <span>
                          Assigned{" "}
                          <span style={{ fontWeight: "500", color: "#444" }}>
                            {formatDate(item.assignedOn)}{" "}
                            {formatTime(item.assignedOn)}
                          </span>
                        </span>
                        <span style={{ textAlign: "right" }}>
                          by{" "}
                          <span style={{ fontWeight: "500", color: "#444" }}>
                            {item.assignedBy || "-"}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── History ───────────────────────────────────────── */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "18px",
                padding: "20px 18px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#111",
                  margin: "0 0 16px",
                }}
              >
                History
              </p>

              {records.length === 0 ? (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    textAlign: "center",
                    padding: "16px 0",
                    margin: 0,
                  }}
                >
                  No history yet
                </p>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                >
                  {records.map(normalize).map((h) => {
                    const badgeClass =
                      statusColors[h.status] || "bg-gray-100 text-gray-500";
                    const hasReturnInfo =
                      h.status === RETURNED_STATUS || h.status === REFUNDED_STATUS;
                    const hasRefundInfo = h.status === REFUNDED_STATUS;

                    return (
                      <div
                        key={h._id}
                        style={{
                          backgroundColor: "#f7f7fa",
                          border: "1px solid #eee",
                          borderRadius: "12px",
                          padding: "14px 16px",
                        }}
                      >
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide mb-2 ${badgeClass}`}
                        >
                          {h.status || "-"}
                        </span>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                          }}
                        >
                          <AccessoryLogo
                            src={h.accessoryLogo}
                            alt={h.accessoryName}
                          />
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#555",
                              lineHeight: "1.5",
                            }}
                          >
                            <p style={{ margin: 0 }}>
                              <span
                                style={{ fontWeight: "600", color: "#222" }}
                              >
                                {h.accessoryName}
                              </span>
                              <br />
                              Assigned on{" "}
                              <span style={{ fontWeight: "500" }}>
                                {formatDate(h.assignedOn)}{" "}
                                {formatTime(h.assignedOn)}
                              </span>{" "}
                              by{" "}
                              <span style={{ fontWeight: "500" }}>
                                {h.assignedBy || "-"}
                              </span>
                            </p>

                            {h.allotedOn && (
                              <p style={{ margin: 0 }}>
                                ₹{h.originalValue} deducted on{" "}
                                <span style={{ fontWeight: "500" }}>
                                  {formatDate(h.allotedOn)}{" "}
                                  {formatTime(h.allotedOn)}
                                </span>
                              </p>
                            )}

                            {hasReturnInfo && h.returnedOn && (
                              <p style={{ margin: 0 }}>
                                Returned on{" "}
                                <span style={{ fontWeight: "500" }}>
                                  {formatDate(h.returnedOn)}{" "}
                                  {formatTime(h.returnedOn)}
                                </span>{" "}
                                by{" "}
                                <span style={{ fontWeight: "500" }}>
                                  {h.refundBy || "-"}
                                </span>
                                {h.remarks && (
                                  <span style={{ color: "#999" }}>
                                    {" "}
                                    ({h.remarks})
                                  </span>
                                )}
                              </p>
                            )}

                            {hasRefundInfo && h.refundedOn && (
                              <p style={{ margin: 0 }}>
                                <span style={{ fontWeight: "500" }}>
                                  ₹{h.refundedValue}
                                </span>{" "}
                                Refunded on{" "}
                                <span style={{ fontWeight: "500" }}>
                                  {formatDate(h.refundedOn)}{" "}
                                  {formatTime(h.refundedOn)}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEssentials;